import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Alert, AlertIcon,
  Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Text, useToast, Flex,
} from '@chakra-ui/react';
import axios from 'axios';
import { ProjectLeadAssignment } from '../models/project';
import { validateLeads } from '../utils/leadValidation';
import ProjectLeadFields from './ProjectLeadFields';

const fmtMoney = (n: number) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Props {
  workOrderId: string;
  workOrderNumber?: string;
  isOpen: boolean;
  onClose: () => void;
}

// item 526 — assign a work order's project lead(s) before invoicing. Works for a standalone
// service WO (no proposal) as well as proposal-derived ones; the leads drive ProjectLead-
// attribution commissions at invoice finalization. Reuses the proposal wizard's lead picker.
const WorkOrderLeadsModal: React.FC<Props> = ({
  workOrderId, workOrderNumber, isOpen, onClose,
}) => {
  const toast = useToast();
  const [leads, setLeads] = useState<ProjectLeadAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // item 1173 (Chad) — the $1k floor VERDICT, right in the form: whether this job's
  // commission-flagged lines clear the floor (leads pay) or not (recorded, no commission).
  const [basis, setBasis] = useState<{ flaggedTotal: number; floor: number; aboveFloor: boolean; noLeadCommission?: boolean } | null>(null);
  // item 1174 (Chad) — the SIGNED "no lead commission books on this job" declaration
  const [noCommission, setNoCommission] = useState(false);

  useEffect(() => {
    if (!isOpen || !workOrderId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${apiUrl()}WorkOrder/${workOrderId}/leads`)
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data ?? []) as { userId: string; percent: number }[];
        setLeads(rows.map((r) => ({ userId: r.userId, percent: r.percent })));
      })
      .catch(() => { if (!cancelled) setLeads([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(`${apiUrl()}WorkOrder/${workOrderId}/commission-basis`)
      .then((res) => {
        if (cancelled) return;
        setBasis(res.data);
        setNoCommission(!!res.data?.noLeadCommission);
      })
      .catch(() => { if (!cancelled) setBasis(null); });
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [isOpen, workOrderId]);

  const { balanced, complete } = validateLeads(leads);
  // empty = clear (valid); otherwise every row needs a user and the split must total 100%
  const canSave = leads.length === 0 || (balanced && complete);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${apiUrl()}WorkOrder/${workOrderId}/leads`, { leads, noLeadCommission: noCommission });
      toast({ status: 'success', title: leads.length ? 'Project lead saved.' : 'Project lead cleared.' });
      onClose();
    } catch {
      toast({ status: 'error', title: 'Could not save the project lead.', description: 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Assign project lead
          {workOrderNumber ? <Text fontSize="sm" color="gray.500" fontWeight="normal">{workOrderNumber}</Text> : null}
          {/* item 1173 — the floor verdict lives IN the form: assignment is always recorded
              for accountability + the Sales Bible; the floor only gates PAY (item 1143/1149).
              The final invoice is the authoritative judge — this predicts off the WO's lines. */}
          {basis && (
            <Alert
              status={(() => {
                if (noCommission) return 'info';
                return basis.aboveFloor ? 'success' : 'warning';
              })()}
              borderRadius="md"
              mt={2}
              fontSize="sm"
              fontWeight="normal"
            >
              <AlertIcon />
              {(() => {
                if (noCommission) return 'SIGNED: no lead commission books on this job — the assignment stays recorded for accountability.';
                return basis.aboveFloor
                  ? `Lead commission basis ${fmtMoney(basis.flaggedTotal)} (lead-flagged config lines only) — above the ${fmtMoney(basis.floor)} floor: the project lead WILL accrue commission at the final invoice.`
                  : `Lead commission basis ${fmtMoney(basis.flaggedTotal)} (lead-flagged config lines only) — at or under the ${fmtMoney(basis.floor)} floor: the lead is recorded for accountability, but NO lead commission pays on this job (other reasons judge their own lines).`;
              })()}
            </Alert>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Set who leads this job before you invoice it. Project-lead commission pays only the
            assigned lead(s), split by %. Leave empty for no project-lead attribution.
          </Text>
          {loading
            ? <Flex justify="center" py={8}><Spinner /></Flex>
            : <ProjectLeadFields leads={leads} onChange={setLeads} />}
          {/* item 1174 (Chad) — the SIGN: declare no-lead-commission on THIS job, any amount.
              The assignment above stays recorded; only the payout is signed away. */}
          <Checkbox
            mt={4}
            isChecked={noCommission}
            onChange={(e) => setNoCommission(e.target.checked)}
          >
            <Text fontSize="sm">
              Book NO lead commission on this job — assignment stays recorded for accountability
            </Text>
          </Checkbox>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={loading || !canSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

WorkOrderLeadsModal.defaultProps = {
  workOrderNumber: undefined,
};

export default WorkOrderLeadsModal;
